import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { PHBHPLayoutPackageService } from 'ph-bhp-feature-packages/ph-bhp-layout-package/public_api';
import { TranslationKeys } from 'ph-bhp-localization';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Question, QuestionnaireDTO, QuestionType } from 'ph-bhp-feature-packages/ph-bhp-feature-package-check/lib/models/check-interface';
import { ChecksService } from 'ph-bhp-feature-packages/ph-bhp-feature-package-check/public_api';
import { generateId } from 'ph-bhp-feature-packages/ph-bhp-feature-package-check/lib/utils/utils';
import { AppConfig } from 'src/app/app.config';
import { questionTypesMap } from 'ph-bhp-feature-packages/ph-bhp-feature-package-evaluation/lib/ph-bhp-questionTypes.service';

@Component({
  selector: 'hygiene-check-question-manager',
  templateUrl: './hygiene-check-question-manager.component.html',
  styleUrls: ['./hygiene-check-question-manager.component.scss']
})
export class HygieneCheckQuestionManagerComponent implements OnInit {

  @Input() questionnaire: QuestionnaireDTO;
  @Input() editQuestion: Question;
  @Input() questionTypes: QuestionType[] = [];
  @Output() closeQuestionManager = new EventEmitter<Question>();

  translationKeys: typeof TranslationKeys = TranslationKeys;
  
  mode: string;
  questionId = '';
  questionType: QuestionType;
  questionTypeId = '';
  questionTypeName = '';
  showTheme = false;
  formData: UntypedFormGroup;
  modalRef: BsModalRef;
  defaultAssessmentParagraph = true;
  options: any[] = [];
  dummyMultiselectOptions: any[] = [
    {text: 'Option 1', value: 'option1'},
    {text: 'Option 2', value: 'option2'},
  ]

 

  @ViewChild('cancelmodal') cancelmodal: TemplateRef<any>;

  constructor(
    public layoutService: PHBHPLayoutPackageService,
    private modalService: BsModalService,
    public translate: TranslateService,
    private config: AppConfig,    
    public checksService: ChecksService,
  ) { 
   
  }

  ngOnInit(): void {
    
    this.loadQuestion();
  }

  loadQuestion() {
    if(!this.editQuestion){
      this.mode = 'new';
      this.formData = this.getEmptyFormGroup();
    }else{
      this.mode = 'edit';
      this.formData = this.populateFormGroup(this.editQuestion);
      this.questionType = this.questionTypes.find(x => x.id === this.editQuestion.questionTypeId);
      this.onQuestionTypeChange(this.questionType);

    }
  }

  goBack(){
    this.closeQuestionManager.emit();
  }

  switchControl(controlName: string){
    this.formData.controls[controlName].setValue(!this.formData.controls[controlName].value);
  }

  onQuestionTypeChange(questionType){
    this.questionType = this.questionTypes.find(x => x.id === questionType.id);
    this.questionTypeId = questionType.id;
    this.questionTypeName = questionType.questionTypeName;
    this.formData.controls['questionTypeId'].setValue(this.questionType['id']);
    if(this.questionType.skin === 'Radio' 
      || this.questionType.skin === 'Checkbox'
      || this.questionType.skin === 'Star'){
      this.setRadioOptions(this.questionType);
    }
    if(!this.questionType.showReferenceDesc){
      this.formData.controls['referenceDesc'].setErrors(null);
    }
  }

  defaultAssessment(value){
    this.defaultAssessmentParagraph = value;
    this.formData.controls['defaultAssessmentParagraph'].setValue(value);
    if(value){
      this.formData.controls['assessmentParagraph'].setErrors(null);
    }
    
  }

  setRadioOptions(questionType){
      // maybe min and/or max are negative
      if (!questionType || questionType.min === null || questionType.max === null) {
          return [];
      }

      this.options = [];
      for (let i = questionType.min; i <= questionType.max; i++) {
          this.options.push({
              id: `option-${i}`,
              questionTypeId: questionType.id,
              caption: i.toString(),
              position: i
          });
      }
  }

  saveQuestion(){

    if(!this.formData.get('showFormalBasis').value){
      this.formData.controls['infoFormalBasis'].setErrors(null);
    }
    if(!this.formData.get('showInfo').value){
      this.formData.controls['infoSection'].setErrors(null);
    }

    if(this.formData.valid){

      
      let question:Question = {
        id: this.editQuestion ? this.editQuestion.id : generateId(),
        statement: this.formData.get('statement').value,
        questionTypeId: this.formData.get('questionTypeId').value,
        referenceDesc: this.formData.get('referenceDesc').value,
        showInfo: this.formData.get('showInfo').value,
        infoSection: this.formData.get('infoSection').value,
        showFormalBasis: this.formData.get('showFormalBasis').value,
        infoFormalBasis: this.formData.get('infoFormalBasis').value,
        themeSection: this.formData.get('themeSection').value,
        defaultAssessmentParagraph: this.formData.get('defaultAssessmentParagraph').value,
        assessmentParagraph: this.formData.get('assessmentParagraph').value,
      }
      if(this.mode == 'new'){
        question.createdBy = this.config.userData.userId;

        this.questionnaire.questions.push(question);
        this.closeQuestionManager.emit();
      }else{
        question.updatedBy = this.config.userData.userId;
        question.updatedDate = new Date();
        this.closeQuestionManager.emit(question);
      }
      
    }else{
      this.markFormGroupTouched(this.formData);
    }
  }
 
  markFormGroupTouched(formGroup: UntypedFormGroup) {
      Object.keys(formGroup.controls).forEach(controlName => {
        const control = formGroup.controls[controlName];
        const isVisible = this.isControlVisible(controlName);

        if (control instanceof UntypedFormControl) {
            if (isVisible) {
                control.markAsTouched();
            }
        } else if (control instanceof UntypedFormGroup) {
            this.markFormGroupTouched(control);
        }
    });
  }

  isControlVisible(controlName: string): boolean {
    switch (controlName) {
        case 'infoSection':
            return this.formData.get('showInfo').value;
        case 'infoFormalBasis':
            return this.formData.get('showFormalBasis').value;
        case 'assessmentParagraph':
            return !this.formData.get('defaultAssessmentParagraph').value;
        case 'referenceDesc':
            return this.questionType?.showReferenceDesc;
        default:
            return true;
    }
  }

  cancel(cancelmodal: TemplateRef<any>) {
    if(this.formData.dirty){
      this.modalRef = this.modalService.show(cancelmodal, Object.assign({}, { class: 'w-420' })
      );
    }else{
      this.goBack();
    }
  }

  getEmptyFormGroup() : UntypedFormGroup{
    return  new UntypedFormGroup({
      statement: new UntypedFormControl('', Validators.compose([Validators.required])),
      questionTypeId: new UntypedFormControl('', Validators.required),
      referenceDesc: new UntypedFormControl(''),
      showInfo: new UntypedFormControl(false),
      infoSection: new UntypedFormControl(''),
      showFormalBasis: new UntypedFormControl(false),
      infoFormalBasis: new UntypedFormControl(''),
      themeSection: new UntypedFormControl('', Validators.required),
      defaultAssessmentParagraph: new UntypedFormControl(false),
      assessmentParagraph: new UntypedFormControl(''),
    });
  }

  populateFormGroup(question: any): UntypedFormGroup {

    return new UntypedFormGroup({
      statement: new UntypedFormControl(question.statement, Validators.compose([Validators.required])),
      questionTypeId: new UntypedFormControl(question.questionTypeId, Validators.required),
      referenceDesc: new UntypedFormControl(question.referenceDesc),
      showInfo: new UntypedFormControl(question.showInfo),
      infoSection: new UntypedFormControl(question.infoSection),
      showFormalBasis: new UntypedFormControl(question.showFormalBasis),
      infoFormalBasis: new UntypedFormControl(question.infoFormalBasis),
      themeSection: new UntypedFormControl(question.themeSection, Validators.required),
      defaultAssessmentParagraph: new UntypedFormControl(question.defaultAssessmentParagraph),
      assessmentParagraph: new UntypedFormControl(question.assessmentParagraph),
    });
    
  }

  autoGrowTextZone(e: any) {
    if (e.code === 'Enter') {
      e.target.style.height = (26 + e.target.scrollHeight) + "px";
    }
  }

  isYesNoQuestion() {
    var yesNoQuestion = questionTypesMap.find(x => x.key === 'yesNo');
    console.error('isYesNoQuestion', this.questionTypeId, yesNoQuestion.id);
    return this.questionTypeId.toLowerCase() === yesNoQuestion.id.toLowerCase();
  }
}
